// src/helpers/sanitizeDashboardData.js

const isUnknownString = (value) => typeof value === "string" && value.toLowerCase() === "unknown";

/**
 * Trim a single Mosaic "info" item down to only what we care about.
 * Shared between employees and customers.
 *
 * Final kept shape:
 * {
 *   mosaic_name,
 *   household_size,
 *   majority_age_range,
 *   bilingual,
 *   married,
 *   with_kids,
 *   without_kids,
 *   home_ownership,
 *   renter,
 *   low_income,
 *   high_income
 * }
 */
const sanitizeMosaicInfoItem = (item) => {
	if (!item || typeof item !== "object") return item;

	const {
		mosaic_name,
		household_size,
		majority_age_range,
		bilingual,
		married,
		with_kids,
		without_kids,
		home_ownership,
		renter,
		low_income,
		high_income,
	} = item;

	return {
		mosaic_name,
		household_size,
		majority_age_range,
		bilingual,
		married,
		with_kids,
		without_kids,
		home_ownership,
		renter,
		low_income,
		high_income,
	};
};

/**
 * Sanitize the mosaic object itself:
 * - If household_id/type/group are "Unknown" → drop mosaic entirely (return undefined)
 * - Else:
 *   - Remove household_id, type, group
 *   - Trim info[] with sanitizeMosaicInfoItem
 */
const sanitizeMosaic = (mosaic) => {
	if (!mosaic || typeof mosaic !== "object") return undefined;

	const { household_id, type, group } = mosaic;

	// Drop completely if it's an "Unknown" mosaic
	if (isUnknownString(household_id) || isUnknownString(type) || isUnknownString(group)) {
		return undefined;
	}

	const { info, household_id: _h, type: _t, group: _g, ...rest } = mosaic;

	let sanitized = { ...rest };

	if (Array.isArray(info)) {
		sanitized.info = info.map(sanitizeMosaicInfoItem);
	}

	return sanitized;
};

/**
 * Sanitize the proposed_location_data_tt map:
 * - For each entry, remove lengthInMeters if it is exactly 0.
 */
const sanitizeProposedLocationTTMap = (map) => {
	if (!map || typeof map !== "object") return map;

	const cleaned = {};

	for (const [key, value] of Object.entries(map)) {
		if (!value || typeof value !== "object") {
			cleaned[key] = value;
			continue;
		}

		const { lengthInMeters, ...rest } = value;

		// Only drop lengthInMeters when it is 0; keep it otherwise
		if (lengthInMeters === 0) {
			cleaned[key] = { ...rest };
		} else {
			cleaned[key] = { lengthInMeters, ...rest };
		}
	}

	return cleaned;
};

const sanitizeProposedLocation = (location) => {
	if (!location || typeof location !== "object") return location;

	// Give me everthing except what's listed below
	const {
		_id,
		pdfs,
		bi_dashboard_id,
		geolocation,
		updated_at,
		created_at,
		employee_short_distance,
		employee_long_distance,
		customer_short_distance,
		customer_long_distance,
		notes,
		status,
		opex_value,
		car_trailer_parking,
		parking_ratio,
		available_sf,
		building_sf,
		office_sf,
		clear_height,
		docs_grade_level_doors,
		base_rent,
		percent_leased,
		electric_price_sf,
		gross_price_sf,
		year_built,
		location_suite,
		leasing_company,
		...rest
	} = location;

	const cleaned = { ...rest };

	// Remove name only if blank / equal to ""
	if (typeof cleaned.name === "string" && cleaned.name.trim() === "") {
		delete cleaned.name;
	}

	return cleaned;
};

const sanitizeEmployee = (employee) => {
	if (!employee || typeof employee !== "object") return employee;

	const { _id, bi_dashboard_id, is_sync, updated_at, zip_code, mosaic, ...rest } = employee;

	const hasProposedLocationData =
		Array.isArray(employee.proposed_location_data) && employee.proposed_location_data.length > 0;

	const hasProposedLocationDataTt =
		employee.proposed_location_data_tt &&
		typeof employee.proposed_location_data_tt === "object" &&
		Object.keys(employee.proposed_location_data_tt).length > 0;

	const sanitized = { ...rest };

	// Remove current_location_data if proposed_location_data is present/populated
	if (hasProposedLocationData) {
		delete sanitized.current_location_data;
	}

	// Remove proposed_location_data if proposed_location_data_tt is present/populated
	if (hasProposedLocationDataTt) {
		delete sanitized.proposed_location_data;
	}

	// Trim proposed_location_data_tt so lengthInMeters is removed when 0
	if (employee.proposed_location_data_tt) {
		sanitized.proposed_location_data_tt = sanitizeProposedLocationTTMap(employee.proposed_location_data_tt);
	}

	// Mosaic trimming
	const sanitizedMosaic = sanitizeMosaic(mosaic);
	if (sanitizedMosaic) {
		sanitized.mosaic = sanitizedMosaic;
	}

	// Remove unreachable_locations if null OR empty array
	if (
		employee.unreachable_locations == null ||
		(Array.isArray(employee.unreachable_locations) && employee.unreachable_locations.length === 0)
	) {
		delete sanitized.unreachable_locations;
	}

	return sanitized;
};

const sanitizeCustomer = (customer) => {
	if (!customer || typeof customer !== "object") return customer;

	const {
		// Remove
		_id,
		bi_dashboard_id,
		is_sync,
		updated_at,
		mosaic,
		...rest
	} = customer;

	const sanitized = { ...rest };

	const hasProposedLocationDataTt =
		customer.proposed_location_data_tt &&
		typeof customer.proposed_location_data_tt === "object" &&
		Object.keys(customer.proposed_location_data_tt).length > 0;

	// Remove current_location_data if proposed_location_data_tt has data
	if (hasProposedLocationDataTt) {
		delete sanitized.current_location_data;
	}

	// Remove proposed_location_data if proposed_location_data_tt has data
	if (hasProposedLocationDataTt) {
		delete sanitized.proposed_location_data;
	}

	// Trim proposed_location_data_tt so lengthInMeters is removed when 0
	if (customer.proposed_location_data_tt) {
		sanitized.proposed_location_data_tt = sanitizeProposedLocationTTMap(customer.proposed_location_data_tt);
	}

	// Remove unreachable_locations if empty array
	if (Array.isArray(customer.unreachable_locations) && customer.unreachable_locations.length === 0) {
		delete sanitized.unreachable_locations;
	}

	// Mosaic trimming
	const sanitizedMosaic = sanitizeMosaic(mosaic);
	if (sanitizedMosaic) {
		sanitized.mosaic = sanitizedMosaic;
	}

	return sanitized;
};

export const sanitizeDashboardData = (data) => {
	if (!data || typeof data !== "object") return data;

	// These dashboard types are already sanitized before being passed in.
	// Return them as-is so the existing logic below does not strip their fields.
	const newDashboardTypes = ["community_insights", "street_insights", "national_insights"];
	if (newDashboardTypes.includes(data.dashboard_type)) {
		return data;
	}

	const {
		commute_type,
		locationToCompare,
		locationToCompareName,
		id,
		dashboard_title,
		max_traveltime_seconds,
		is_industrial_location_type,
		latitude,
		longitude,
		is_process_completed,
		current_location_drivetime_average,
		updated_at,
		rolePermissions,
		scoreInsightsDashboards,
		...rest
	} = data;

	const sanitized = {
		...rest,
	};

	const hasProposedLocations = Array.isArray(data.proposedLocations) && data.proposedLocations.length > 0;

	// If proposedLocations has data, remove these root-level fields:
	// - current_location
	// - notes
	// - status
	// - leasing_company
	if (hasProposedLocations) {
		delete sanitized.current_location;
		delete sanitized.notes;
		delete sanitized.status;
		delete sanitized.leasing_company;
	}

	// Remove root-level gross/opex when 0
	if (sanitized.gross_price_sf === 0) {
		delete sanitized.gross_price_sf;
	}
	if (sanitized.opex_value === 0) {
		delete sanitized.opex_value;
	}

	// Always remove mosaic_cluster_groups
	if ("mosaic_cluster_groups" in sanitized) {
		delete sanitized.mosaic_cluster_groups;
	}

	// Sanitize soc_codes: keep only soc_name
	if (Array.isArray(data.soc_codes)) {
		sanitized.soc_codes = data.soc_codes.map((code) => ({
			soc_name: code.soc_name,
		}));
	}

	// Sanitize proposedLocations
	if (Array.isArray(data.proposedLocations)) {
		sanitized.proposedLocations = data.proposedLocations.map(sanitizeProposedLocation);
	}

	// Sanitize townsonEmployee
	if (Array.isArray(data.townsonEmployee)) {
		sanitized.townsonEmployee = data.townsonEmployee.map(sanitizeEmployee);
	}

	// Sanitize customers
	if (Array.isArray(data.customers)) {
		sanitized.customers = data.customers.map(sanitizeCustomer);
	}

	return sanitized;
};

// ---------------------------------------------------------------------------
// Private helpers for shaping chart data into clean AI-readable objects
// ---------------------------------------------------------------------------

/**
 * Convert the market overview array into clean {label, value} objects.
 * Strips the internal 'toltip' typo field the AI doesn't need.
 *
 * Input:  [{ series: "8,108,497", label: "Population", toltip: "" }, ...]
 * Output: [{ label: "Population", value: "8,108,497" }, ...]
 */
const cleanMarketOverview = (data) => {
	if (!Array.isArray(data)) return null;
	return data.map(({ series, label }) => ({ label, value: series }));
};

/**
 * Convert age distribution from internal label_name/label_value keys to label/value.
 *
 * Input:  [{ label_name: "0 - 4", label_value: 509363 }, ...]
 * Output: [{ label: "0 - 4", value: 509363 }, ...]
 */
const cleanAgeDistribution = (data) => {
	if (!Array.isArray(data)) return null;
	return data.map(({ label_name, label_value }) => ({ label: label_name, value: label_value }));
};

/**
 * Zip parallel-array chart data into [{label, value}] objects so the AI can
 * read which value belongs to which label.
 *
 * Input:  { series: [34.52, 28.36, 37.12], label: ["Bachelors/Grad", ...] }
 * Output: [{ label: "Bachelors/Grad", value: 34.52 }, ...]
 */
const zipSeriesAndLabels = (data) => {
	if (!data || !Array.isArray(data.series) || !Array.isArray(data.label)) return null;
	return data.label.map((label, index) => ({ label, value: data.series[index] ?? null }));
};

// ---------------------------------------------------------------------------

/**
 * Build the AI payload for Community Insights.
 *
 * @param {object} data             - The component's data state (API response)
 * @param {object} occupation       - Selected SOC code: { soc_code, soc_name }
 * @param {object} occupation2      - Selected MSA: { id, name }
 * @param {number} driveTime        - Drive time in minutes (15, 30, 45)
 * @param {object} livingWageInput  - { hourlyWage, bluechipLivingWage }
 * @returns {object}
 */
export const sanitizeCommunityInsightsData = (data, occupation, occupation2, driveTime, livingWageInput) => {
	return {
		dashboard_type: "community_insights",
		msa: {
			id: occupation2?.id ?? null,
			name: occupation2?.name ?? null,
		},
		soc_code: {
			code: occupation?.soc_code ?? null,
			name: occupation?.soc_name ?? null,
		},
		drive_time_minutes: driveTime ?? null,
		living_wage_target_percent: livingWageInput?.bluechipLivingWage ?? null,
		target_hourly_wage: livingWageInput?.hourlyWage ?? null,
		// How concentrated this occupation is here versus the national average.
		location_quotient: extractLocationQuotient(data?.marketOccupationOverviewData),
		// Year-by-year job counts for the selected occupation (2021 through 2028).
		occupation_jobs_projection: zipSeriesAndLabels(data?.marketOccupationGrowthData),
		// The most recent year with real job numbers. Years after this are projections.
		latest_actual_year: data?.latestActualYear ?? null,
		market_overview: cleanMarketOverview(data?.marketOverviewData),
		age_distribution: cleanAgeDistribution(data?.marketAgeDistributionData),
		// People aged 15 to 45 compared with people over 45.
		aging_labor_ratio: data?.agingLaborRatio ?? null,
		// Median age for the area (the same value shown on the dashboard).
		median_age: data?.medianAge ?? null,
		education_attainment: zipSeriesAndLabels(data?.marketEducationData),
		race_distribution: zipSeriesAndLabels(data?.marketRaceDistributionData),
		// Share of the population that is Hispanic, as a decimal fraction (0.29 means 29%).
		hispanic_ethnicity_percent: data?.hispanicEthnicityPercent ?? null,
		living_wage_data: zipSeriesAndLabels(data?.marketOccupationWagesData),
		labor_cost: zipSeriesAndLabels(data?.laborCostData),
	};
};
/**
 * Build the AI payload for Street Insights.
 *
 * @param {object} data             - The component's data state (API response)
 * @param {object} selectedLocation - Redux location: { address: { freeformAddress }, position: { lat, lon } }
 * @param {object} occupation       - Selected SOC: { id, name }
 * @param {number} driveTimeId      - The drive time step ID (multiply by 15 to get minutes)
 * @param {object} livingWageInput  - { hourlyWage, bluechipLivingWage }
 * @returns {object}
 */
export const sanitizeStreetInsightsData = (data, selectedLocation, occupation, driveTimeId, livingWageInput) => {
	return {
		dashboard_type: "street_insights",
		address: selectedLocation?.address?.freeformAddress ?? null,
		latitude: selectedLocation?.position?.lat ?? null,
		longitude: selectedLocation?.position?.lon ?? null,
		soc_code: {
			code: occupation?.id ?? null,
			name: occupation?.name ?? null,
		},
		drive_time_minutes: driveTimeId ? driveTimeId * 15 : null,
		living_wage_target_percent: livingWageInput?.bluechipLivingWage ?? null,
		target_hourly_wage: livingWageInput?.hourlyWage ?? null,
		// How concentrated this occupation is here versus the national average.
		location_quotient: extractLocationQuotient(data?.marketOccupationOverviewData),
		// Year-by-year job counts for the selected occupation (2021 through 2028).
		occupation_jobs_projection: zipSeriesAndLabels(data?.marketOccupationGrowthData),
		// The most recent year with real job numbers. Years after this are projections.
		latest_actual_year: data?.latestActualYear ?? null,
		market_overview: cleanMarketOverview(data?.marketOverviewData),
		age_distribution: cleanAgeDistribution(data?.marketAgeDistributionData),
		// People aged 15 to 45 compared with people over 45.
		aging_labor_ratio: data?.agingLaborRatio ?? null,
		// Median age for the area (the same value shown on the dashboard).
		median_age: data?.medianAge ?? null,
		education_attainment: zipSeriesAndLabels(data?.marketEducationData),
		race_distribution: zipSeriesAndLabels(data?.marketRaceDistributionData),
		living_wage_data: zipSeriesAndLabels(data?.marketOccupationWagesData),
		labor_cost: zipSeriesAndLabels(data?.laborCostData),
		who_lives_nearby: data?.nearby_trim ?? null,
	};
};

/**
 * Build the AI payload for National Insights.
 *
 * Handles both state-level and MSA-level comparison modes.
 *
 * @param {boolean} isMsaLevel               - True = MSA mode, False = State mode
 * @param {object}  selectedSOCCodes         - { soc_code, soc_name }
 * @param {Array}   visibleStateProperties   - MSA-level data objects
 * @param {Array}   visibleMainStateProperty - State-level data objects
 * @returns {object}
 */
export const sanitizeNationalInsightsData = (
	isMsaLevel,
	selectedSOCCodes,
	visibleStateProperties,
	visibleMainStateProperty,
) => {
	// Pick whichever list is active based on the current comparison mode
	const selectedLocations = isMsaLevel ? visibleStateProperties ?? [] : visibleMainStateProperty ?? [];

	return {
		dashboard_type: "national_insights",
		is_msa_level: isMsaLevel,
		soc_code: {
			code: selectedSOCCodes?.soc_code ?? null,
			name: selectedSOCCodes?.soc_name ?? null,
		},
		selected_locations: selectedLocations,
	};
};

/**
 * Pull the Location Quotient value out of the occupation overview list.
 *
 * That list also holds job-count rows, but we ignore those here because the
 * year-by-year job counts are already covered by the jobs projection. We only
 * want the single Location Quotient number.
 *
 * Input:  [{ name: "Jobs", value: 36104 }, { name: "Location Quotient", value: 0.92 }]
 * Output: 0.92   (or null when the row is missing)
 */
const extractLocationQuotient = (overviewData) => {
	if (!Array.isArray(overviewData)) return null;
	const match = overviewData.find((item) => item?.name === "Location Quotient");
	return match?.value.toFixed(2) ?? null;
};
